/**
 * CertificateVerify.jsx
 *
 * Route: /verify/:certificateNumber
 *
 *   <Route path="/verify/:certificateNumber" element={<CertificateVerify />} />
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  User,
  BookOpen,
  Building2,
  CalendarClock,
  CalendarDays,
  Hash,
  BadgeCheck,
  AlertCircle,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "https://www.secu-wall.com";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-green-500/10 last:border-none">
      <div className="p-2 bg-green-500/10 rounded-lg text-green-400 mt-0.5 shrink-0">
        <Icon size={16} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-green-300/50">
          {label}
        </span>
        <span className="text-base font-semibold text-[#E6FCEF]">{value}</span>
      </div>
    </div>
  );
}

/* ── Loading skeleton ── */
function LoadingState() {
  return (
    <div className="min-h-screen bg-[#02130C] flex items-center justify-center px-4 selection:bg-green-500/30">
      <div
        className="w-full max-w-lg bg-green-950/90 border border-green-500/20 rounded-[40px] p-10
          flex flex-col items-center text-center gap-6"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(7,66,29,0.12) 3px, transparent 1px)",
          backgroundSize: "6px 100%",
        }}
        aria-live="polite"
      >
        {/* Pulse ring */}
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-green-500/20 border-t-green-400 animate-spin" />
          <ShieldCheck
            className="absolute inset-0 m-auto text-green-500/40"
            size={28}
          />
        </div>
        <div>
          <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
            Verifying Certificate
          </p>
          <p className="text-green-300/40 text-xs mt-1">
            Checking authenticity…
          </p>
        </div>
        {/* Skeleton rows */}
        {[70, 50, 85, 60].map((w, i) => (
          <div
            key={i}
            className="h-3 rounded-full bg-green-500/10 animate-pulse"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Error state ── */
function ErrorState({ message, certNumber }) {
  return (
    <div className="min-h-screen bg-[#02130C] flex items-center justify-center px-4 selection:bg-green-500/30">
      <div
        className="w-full max-w-lg bg-red-950/20 border border-red-500/20 rounded-[40px] p-10
          flex flex-col items-center text-center gap-4"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(120,0,0,0.05) 3px, transparent 1px)",
          backgroundSize: "6px 100%",
        }}
        role="alert"
      >
        <div className="p-4 bg-red-500/10 rounded-full">
          <ShieldX size={40} className="text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white uppercase tracking-tight">
            Verification Failed
          </h1>
          <p className="text-red-300/70 text-sm mt-2 leading-relaxed">{message}</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-xl">
          <Hash size={13} className="text-red-400" />
          <span className="text-red-300/60 text-xs font-mono">{certNumber}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function CertificateVerify() {
  const { certificateNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Block right-click + devtools shortcuts (same as Careers page) ──
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 || // F12
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) || // Ctrl+Shift+I / J
        (e.ctrlKey && e.keyCode === 85) // Ctrl+U
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // ── Fetch certificate data ──
  useEffect(() => {
    if (!certificateNumber) return;
    setLoading(true);
    setError(null);

    axios
      .get(`${API_BASE}/api/qr/${certificateNumber}/`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError("Certificate not found. Please check the QR code and try again.");
        } else {
          setError("Something went wrong. Please try again later.");
        }
        setLoading(false);
      });
  }, [certificateNumber]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} certNumber={certificateNumber} />;

  const isExpired = data.is_expired;

  return (
    <div className="min-h-screen bg-[#02130C] flex items-center justify-center
      px-4 py-16 text-[#E6FCEF] selection:bg-green-500/30">

      <div className="w-full max-w-lg flex flex-col gap-6">

        {/* ── Top label ── */}
        <div className="flex items-center gap-4">
          <div className="flex-grow h-px bg-green-500/20" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-500">
            Certificate Verification
          </span>
          <div className="flex-grow h-px bg-green-500/20" />
        </div>

        {/* ── Main card ── */}
        <div
          className={`relative overflow-hidden rounded-[40px] border p-10
            ${isExpired
              ? "bg-red-950/10 border-red-500/20"
              : "bg-green-950/90 border-green-500/20 shadow-[0_0_60px_rgba(34,197,94,0.07)]"
            }`}
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(7,66,29,0.12) 3px, transparent 1px)",
            backgroundSize: "6px 100%",
          }}
        >
          {/* Glow accent top-right */}
          <div
            className={`absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-20 pointer-events-none
              ${isExpired ? "bg-red-500" : "bg-green-500"}`}
          />

          {/* ── Status header ── */}
          <div className="flex items-center gap-5 mb-8">
            <div
              className={`p-4 rounded-2xl shrink-0
                ${isExpired ? "bg-red-500/10" : "bg-green-500/10"}`}
            >
              {isExpired ? (
                <ShieldAlert size={36} className="text-red-400" />
              ) : (
                <ShieldCheck size={36} className="text-green-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-black text-white uppercase tracking-tight">
                {isExpired ? "Certificate Expired" : "Verified Certificate"}
              </h1>
              <div className="flex items-center gap-2 mt-1.5">
                {isExpired ? (
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-black uppercase tracking-widest">
                    <AlertCircle size={10} /> Expired
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-[10px] font-black uppercase tracking-widest">
                    <BadgeCheck size={10} /> Valid
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Certificate number chip ── */}
          <div
            className="flex items-center gap-3 px-5 py-3.5 mb-8
              bg-[#04150D]/70 border border-green-500/20 rounded-2xl"
          >
            <Hash size={15} className="text-green-400 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-green-300/50">
                Certificate Number
              </span>
              <span className="font-mono font-bold text-white tracking-wider text-sm">
                {data.certificate_number}
              </span>
            </div>
          </div>

          {/* ── Info rows ── */}
          <div className="flex flex-col">
            <InfoRow icon={User}         label="Issued To"    value={data.name} />
            <InfoRow icon={BookOpen}     label="Course"       value={data.course_name} />
            <InfoRow icon={Building2}    label="Organisation" value={data.organization} />
            <InfoRow icon={CalendarClock} label="Expiry Date" value={formatDate(data.expiry_date)} />
            <InfoRow icon={CalendarDays} label="Issue Date"  value={formatDate(data.issue_date)} />
          </div>

          {/* ── Footer message ── */}
          <div
            className={`mt-8 pt-6 border-t ${isExpired ? "border-red-500/10" : "border-green-500/10"}`}
          >
            <p
              className={`text-xs text-center leading-relaxed font-medium italic
                ${isExpired ? "text-red-300/50" : "text-green-300/40"}`}
            >
              {isExpired
                ? "This certificate has expired and is no longer valid."
                : "This certificate is authentic and currently valid. Powered by our secure QR verification system."}
            </p>
          </div>
        </div>

        {/* ── Bottom label ── */}
        <div className="flex items-center gap-4">
          <div className="flex-grow h-px bg-green-500/20" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-green-300/30">
            Secure Verification
          </span>
          <div className="flex-grow h-px bg-green-500/20" />
        </div>

      </div>
    </div>
  );
}